import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography } from 'antd';
import { TrophyOutlined, ReloadOutlined, StarOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const MemoryGame = ({ onGameComplete }) => {
    const [cards, setCards] = useState([]);
    const [flippedCards, setFlippedCards] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [moves, setMoves] = useState(0);
    const [showStartScreen, setShowStartScreen] = useState(true);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameEnded, setGameEnded] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [score, setScore] = useState(0);
    const [timeElapsed, setTimeElapsed] = useState(0);
    
    // Audio refs
    const backgroundAudioRef = useRef(null);
    const winnerAudioRef = useRef(null);
    const yayAudioRef = useRef(null);
    const clickAudioRef = useRef(null);

    // Emoji pairs for the memory game - Maveshi (Livestock) emojis
    const emojiPairs = ['🐄', '🐂', '🐃', '🐑', '🐐', '🐎', '🐴', '🫏', '🐔', '🐓', '🦆', '🦃', '🐷', '🐽', '🐮', '🐗'];

    // Initialize audio files
    useEffect(() => {
        backgroundAudioRef.current = new Audio('/sounds/memory-background.mp3');
        backgroundAudioRef.current.volume = 0.4; // Set volume to 40% for background music
        backgroundAudioRef.current.loop = true; // Loop the background music
        
        winnerAudioRef.current = new Audio('/sounds/winner.mp3');
        winnerAudioRef.current.volume = 0.7; // Set volume to 70%
        
        yayAudioRef.current = new Audio('/sounds/yay.mp3');
        yayAudioRef.current.volume = 0.7; // Set volume to 70%
        
        clickAudioRef.current = new Audio('/sounds/snap.mp3');
        clickAudioRef.current.volume = 0.6; // Set volume to 60%
        
        return () => {
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
                backgroundAudioRef.current = null;
            }
            if (winnerAudioRef.current) {
                winnerAudioRef.current.pause();
                winnerAudioRef.current = null;
            }
            if (yayAudioRef.current) {
                yayAudioRef.current.pause();
                yayAudioRef.current = null;
            }
            if (clickAudioRef.current) {
                clickAudioRef.current.pause();
                clickAudioRef.current = null;
            }
        };
    }, []);

    // Initialize game
    const initializeGame = () => {
        setShowStartScreen(false);
        // Select 8 random emoji pairs (16 cards total)
        const selectedEmojis = emojiPairs.slice(0, 8);
        const gameCards = [...selectedEmojis, ...selectedEmojis]
            .map((emoji, index) => ({
                id: index,
                emoji,
                isFlipped: false,
                isMatched: false
            }))
            .sort(() => Math.random() - 0.5); // Shuffle cards

        setCards(gameCards);
        setFlippedCards([]);
        setMatchedPairs([]);
        setMoves(0);
        setScore(0);
        setTimeElapsed(0);
        setGameEnded(false);
        setShowResults(false);
        setGameStarted(true);
        
        // Start background music
        if (backgroundAudioRef.current) {
            try {
                backgroundAudioRef.current.currentTime = 0; // Reset to start
                backgroundAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play background music:', error);
                });
            } catch (error) {
                console.log('Error playing background music:', error);
            }
        }
    };

    // Handle card click
    const handleCardClick = (cardId) => {
        if (gameEnded || !gameStarted) return;
        
        const card = cards.find(c => c.id === cardId);
        
        // Don't allow clicking if card is already flipped or matched
        if (card.isFlipped || card.isMatched || flippedCards.length >= 2) {
            return;
        }

        // Play click sound
        if (clickAudioRef.current) {
            try {
                clickAudioRef.current.currentTime = 0; // Reset to start
                clickAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play click sound:', error);
                });
            } catch (error) {
                console.log('Error playing click sound:', error);
            }
        }

        // Flip the card
        const newCards = cards.map(c =>
            c.id === cardId ? { ...c, isFlipped: true } : c
        );
        setCards(newCards);
        setFlippedCards([...flippedCards, cardId]);

        // If two cards are flipped, check for match
        if (flippedCards.length === 1) {
            const firstCard = cards.find(c => c.id === flippedCards[0]);
            const secondCard = card;
            
            setMoves(prev => prev + 1);

            if (firstCard.emoji === secondCard.emoji) {
                // Match found! Play winner sound
                if (winnerAudioRef.current) {
                    try {
                        winnerAudioRef.current.currentTime = 0; // Reset to start
                        winnerAudioRef.current.play().catch(error => {
                            console.log('Could not play winner sound:', error);
                        });
                    } catch (error) {
                        console.log('Error playing winner sound:', error);
                    }
                }
                
                setTimeout(() => {
                    const updatedCards = newCards.map(c =>
                        c.id === firstCard.id || c.id === secondCard.id
                            ? { ...c, isMatched: true, isFlipped: true }
                            : c
                    );
                    setCards(updatedCards);
                    setMatchedPairs([...matchedPairs, firstCard.emoji]);
                    setFlippedCards([]);
                }, 500);
            } else {
                // No match - flip back
                setTimeout(() => {
                    const updatedCards = newCards.map(c =>
                        c.id === firstCard.id || c.id === secondCard.id
                            ? { ...c, isFlipped: false }
                            : c
                    );
                    setCards(updatedCards);
                    setFlippedCards([]);
                }, 1000);
            }
        }
    };

    // Timer
    useEffect(() => {
        let interval = null;
        if (gameStarted && !gameEnded) {
            interval = setInterval(() => {
                setTimeElapsed(prev => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [gameStarted, gameEnded]);

    // Check for game completion
    useEffect(() => {
        if (matchedPairs.length === 8 && gameStarted && !gameEnded) {
            setGameEnded(true);
            
            // Stop background music
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
                backgroundAudioRef.current.currentTime = 0;
            }
            
            // Play yay sound when game finishes
            if (yayAudioRef.current) {
                try {
                    yayAudioRef.current.currentTime = 0;
                    yayAudioRef.current.play().catch(error => {
                        console.log('Could not play yay sound:', error);
                    });
                } catch (error) {
                    console.log('Error playing yay sound:', error);
                }
            }
            
            // Calculate final score: base score (800 for 8 pairs) + bonuses
            const baseScore = 800; // 100 points per matched pair
            const timeBonus = Math.max(0, 500 - timeElapsed * 3);
            const movesBonus = Math.max(0, 400 - moves * 5);
            const finalScore = baseScore + timeBonus + movesBonus;
            setScore(finalScore);
            
            if (onGameComplete) {
                setTimeout(() => onGameComplete(finalScore), 1000);
            }
        }
    }, [matchedPairs.length, gameStarted, gameEnded, timeElapsed, moves, onGameComplete]);

    // Show results after 1 second when game ends
    useEffect(() => {
        if (gameEnded) {
            const timer = setTimeout(() => {
                setShowResults(true);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setShowResults(false);
        }
    }, [gameEnded]);

    // Automatically start a new game after showing results for 2.5 seconds
    useEffect(() => {
        if (showResults && gameStarted) {
            const timer = setTimeout(() => {
                initializeGame();
            }, 2500); // Start new game after 2.5 seconds of showing results
            return () => clearTimeout(timer);
        }
    }, [showResults, gameStarted]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Cover page with splash screen image
    if (showStartScreen && !gameStarted) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '0',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                overflow: 'hidden'
            }}>
                {/* Game Preview Image */}
                <div style={{
                    width: '100%',
                    height: '400px',
                    background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 30%, #98D8C8 70%, #90EE90 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img 
                        src="/assets/images/maveshi-memory.jpg" 
                        alt="Maveshi Memory Game"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center'
                        }}
                        onError={(e) => {
                            // Fallback if image doesn't exist
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
                
                {/* Start Button Section */}
                <div style={{
                    padding: '20px 16px',
                    background: 'rgba(255, 255, 255, 0.95)'
                }}>
                    <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '16px' }}>
                        🧠 Maveshi Memory Game
                    </Title>
                    <Text style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        display: 'block',
                        marginBottom: '12px'
                    }}>
                        Match pairs of livestock cards to win! Test your memory skills.
                    </Text>
                    <Button
                        type="primary"
                        size="middle"
                        icon={<TrophyOutlined />}
                        onClick={initializeGame}
                        style={{
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '0 20px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Start Game
                    </Button>
                </div>
            </div>
        );
    }

    if (!gameStarted) {
        return null;
    }

    return (
        <div style={{ width: '100%', position: 'relative' }}>
            {/* Backdrop Overlay */}
            {showResults && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 50,
                    borderRadius: '10px'
                }} />
            )}

            {/* Game Over Message - Overlay in Middle */}
            {showResults && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 100,
                    textAlign: 'center',
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                    borderRadius: '10px',
                    border: '2px solid #52c41a',
                    boxShadow: '0 4px 12px rgba(82, 196, 26, 0.2)',
                    maxWidth: '380px',
                    width: 'calc(100% - 32px)'
                }}>
                    <TrophyOutlined style={{ fontSize: '28px', color: '#52c41a', marginBottom: '6px' }} />
                    <Title level={5} style={{ margin: '0 0 4px 0', color: '#52c41a', fontSize: '16px' }}>
                        🎉 Congratulations!
                    </Title>
                    <Text style={{ fontSize: '12px', color: '#389e0d', display: 'block', marginBottom: '8px' }}>
                        You matched all pairs!
                    </Text>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                        marginTop: '8px'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <Text style={{ fontSize: '10px', color: '#666', display: 'block' }}>Moves</Text>
                            <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>{moves}</Text>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <Text style={{ fontSize: '10px', color: '#666', display: 'block' }}>Time</Text>
                            <Text style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>{formatTime(timeElapsed)}</Text>
                        </div>
                    </div>
                    <Text style={{ 
                        fontSize: '11px', 
                        color: '#666',
                        display: 'block',
                        marginTop: '10px',
                        fontStyle: 'italic'
                    }}>
                        New game starting...
                    </Text>
                </div>
            )}

            {/* Game Stats */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
                padding: '8px 12px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                flexWrap: 'wrap',
                gap: '8px'
            }}>
                <div style={{ textAlign: 'center', flex: 1, minWidth: '70px' }}>
                    <Text style={{ fontSize: '10px', color: '#666', display: 'block', fontWeight: 600 }}>
                        Time
                    </Text>
                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                        {formatTime(timeElapsed)}
                    </Text>
                </div>
                <div style={{ textAlign: 'center', flex: 1, minWidth: '70px' }}>
                    <Text style={{ fontSize: '10px', color: '#666', display: 'block', fontWeight: 600 }}>
                        Moves
                    </Text>
                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                        {moves}
                    </Text>
                </div>
                <div style={{ textAlign: 'center', flex: 1, minWidth: '70px' }}>
                    <Text style={{ fontSize: '10px', color: '#666', display: 'block', fontWeight: 600 }}>
                        Matched
                    </Text>
                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                        {matchedPairs.length}/8
                    </Text>
                </div>
            </div>

            {/* Game Board */}
            <div className="memory-game-board" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
                marginBottom: '12px',
                padding: '10px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '10px',
                border: '2px solid rgba(24, 144, 255, 0.3)',
                maxWidth: '380px',
                margin: '0 auto 12px auto',
                opacity: showResults ? 0.4 : 1,
                transition: 'opacity 0.3s ease',
                pointerEvents: showResults ? 'none' : 'auto'
            }}>
                {cards.map(card => (
                    <div
                        key={card.id}
                        className={`memory-card ${!card.isFlipped && !card.isMatched ? 'memory-card-unflipped' : ''}`}
                        onClick={() => handleCardClick(card.id)}
                        style={{
                            aspectRatio: '1',
                            background: card.isMatched 
                                ? 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)'
                                : card.isFlipped
                                ? 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                                : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            borderRadius: '8px',
                            cursor: card.isMatched ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: card.isFlipped || card.isMatched ? '40px' : '20px',
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease',
                            boxShadow: card.isMatched 
                                ? '0 4px 12px rgba(82, 196, 26, 0.3)'
                                : card.isFlipped
                                ? '0 4px 12px rgba(24, 144, 255, 0.3)'
                                : '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transform: card.isFlipped || card.isMatched ? 'scale(1)' : 'scale(1)',
                            border: card.isMatched 
                                ? '2px solid #52c41a'
                                : card.isFlipped
                                ? '2px solid #1890ff'
                                : '2px solid rgba(24, 144, 255, 0.3)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                            if (!card.isFlipped && !card.isMatched) {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.4)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!card.isFlipped && !card.isMatched) {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                            }
                        }}
                    >
                        {card.isFlipped || card.isMatched ? (
                            <span style={{ 
                                animation: card.isMatched ? 'none' : 'flip-in 0.3s ease'
                            }}>
                                {card.emoji}
                            </span>
                        ) : (
                            <span style={{ color: 'white', opacity: 0.8 }}>?</span>
                        )}
                        {card.isMatched && (
                            <div style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                fontSize: '12px',
                                color: '#52c41a'
                            }}>
                                ✓
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <style jsx>{`
                @keyframes flip-in {
                    0% {
                        transform: rotateY(90deg);
                        opacity: 0;
                    }
                    100% {
                        transform: rotateY(0deg);
                        opacity: 1;
                    }
                }
            `}</style>
            <style>{`
                .memory-game-board {
                    max-width: 360px !important;
                }
                
                @media (max-width: 768px) {
                    .memory-game-board {
                        gap: 6px !important;
                        padding: 8px !important;
                        max-width: 320px !important;
                    }
                    
                    .memory-card {
                        font-size: 36px !important;
                        border-radius: 6px !important;
                    }
                    
                    .memory-card-unflipped {
                        font-size: 18px !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .memory-game-board {
                        gap: 5px !important;
                        padding: 6px !important;
                        max-width: 280px !important;
                    }
                    
                    .memory-card {
                        font-size: 32px !important;
                        border-radius: 5px !important;
                    }
                    
                    .memory-card-unflipped {
                        font-size: 16px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default MemoryGame;

